'use client';

import cx from 'classnames';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../design-system/components/form/button/Button';
import { Modal } from '../../../design-system/components/modal/Modal';
import { useModal } from '../../../design-system/components/modal/useModal';
import { Typography } from '../../../design-system/components/typography/Typography';
import { imageLoaderForPath } from '../../../domains/Layout/Image/URL/ImageURL';
import { useShowLoyaltyProgram } from '../../hooks/useShowLoyaltyProgram';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { useFeatureDiscoveryNotification } from '../../providers/user/useFeatureDiscoveryNotification';
import { useUser } from '../../providers/user/useUser';

import style from './LoyaltyProgramModal.module.css';

type Props = {
    seoContext: string;
};

export function LoyaltyProgramModal({ seoContext }: Props) {
    const modalProps = useModal();
    const showLoyaltyProgram = useShowLoyaltyProgram();
    const { user } = useUser();
    const { trackEvent } = useTrackEventAmplitude();
    const { generatePath } = useUrlGenerator();
    const router = useRouter();
    const { canShowMyBenefitsNotification } = useFeatureDiscoveryNotification();
    const { formatMessage } = useIntl();
    const [isTransitioning, setIsTransitioning] = useState(false);

    const notTripFormPage = seoContext !== 'beginForm';
    const notMyBenefitsPage = seoContext !== 'account-my-benefits';
    const showModal = Boolean(
        showLoyaltyProgram &&
            !Cookies.get('loyalty-program-modal-dismissed') &&
            notTripFormPage &&
            notMyBenefitsPage &&
            canShowMyBenefitsNotification,
    );

    useEffect(() => {
        if (showModal) {
            modalProps.open();
        }
    }, [showModal, modalProps]);

    const setCookie = () => {
        Cookies.set('loyalty-program-modal-dismissed', 'true', { expires: 3 });
    };

    const handlePrimaryButtonClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.myBenefitsClicked({
                ...defaultProperties,
                block: 'modal_my_benefits',
            });
        });
        setIsTransitioning(true);
        setTimeout(() => {
            setCookie();
            router.push(generatePath('account_my_benefits', { from: 'loyalty-program-modal' }));
        }, 1500);
    };

    useEffect(() => {
        if (modalProps.isOpen) {
            trackEvent((ampli, defaultProperties) => {
                ampli.benefitsModalDisplayed(defaultProperties);
            });
        }
    }, [modalProps.isOpen, trackEvent]);

    if (!modalProps.isOpen || user === null) {
        return null;
    }

    return (
        <>
            <div
                className={cx(style.transitionOverlay, {
                    [style.active]: isTransitioning,
                })}
            />
            <Modal
                {...modalProps}
                size="small"
                headerVariant="image"
                image={{
                    alt: '',
                    src: '/evaneos-community/modal_header.gif',
                    loader: imageLoaderForPath,
                }}
                close={() => {
                    trackEvent((ampli, defaultProperties) => {
                        ampli.benefitsModalClosed(defaultProperties);
                    });
                    setCookie();
                    modalProps.close();
                }}
            >
                <div className={style.contentContainer}>
                    <Typography as="div" className={style.title} scale="primary-s-extra-bold">
                        {formatMessage(
                            {
                                id: 'global.component.loyaltyProgramModal.title',
                            },
                            {
                                firstname: user.firstName,
                            },
                        )}
                    </Typography>
                    <div className={style.content}>
                        {formatMessage({
                            id: 'global.component.loyaltyProgramModal.description',
                        })}
                    </div>
                </div>
                <div className={style.spacer} />
                <div className={style.buttonsContainer}>
                    <Button
                        priority="tertiary"
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.benefitsModalViewLaterClicked(defaultProperties);
                            });
                            setCookie();
                            modalProps.close();
                        }}
                    >
                        {formatMessage({
                            id: 'global.component.loyaltyProgramModal.laterCta',
                        })}
                    </Button>
                    <Button priority="primary" onClick={handlePrimaryButtonClick}>
                        {formatMessage({
                            id: 'global.component.loyaltyProgramModal.myBenefitsCta',
                        })}
                    </Button>
                </div>
            </Modal>
        </>
    );
}
